import React, { useState, useEffect } from 'react';
import {
    Table, Card, Button, Modal, Form, Input, Switch, Space, Tag,
    Typography, message, Popconfirm, Tooltip, Divider, Row, Col
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined, SecurityScanOutlined,
    ExclamationCircleOutlined, SafetyOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchRoles, addRole, updateRole, deleteRole
} from '../actions';

const { Title, Text } = Typography;
const { confirm } = Modal;

const RoleManagement = ({ roles = [], loading }) => {
    const dispatch = useDispatch();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingRole, setEditingRole] = useState(null);
    const [form] = Form.useForm();

    // Fetch roles when component mounts
    useEffect(() => {
        dispatch(fetchRoles());
    }, [dispatch]);

    // Ensure roles is always an array
    const rolesArray = Array.isArray(roles) ? roles : [];

    const handleAdd = () => {
        setEditingRole(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingRole(record);
        form.setFieldsValue({
            name: record.name,
            description: record.description,
            isActive: record.isActive
        });
        setIsModalVisible(true);
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();

            if (editingRole) {
                // Update role
                const result = await dispatch(updateRole(editingRole.id, values));
                if (result.success) {
                    message.success('Role updated successfully');
                    setIsModalVisible(false);
                    dispatch(fetchRoles()); // Refresh roles list
                } else {
                    message.error(result.error || 'Unable to update role');
                }
            } else {
                // Create new role
                const result = await dispatch(addRole(values));
                if (result.success) {
                    message.success('Role created successfully');
                    setIsModalVisible(false);
                    dispatch(fetchRoles()); // Refresh roles list
                } else {
                    message.error(result.error || 'Unable to create new role');
                }
            }
        } catch (error) {
            console.error('Form validation failed:', error);
        }
    };

    const handleDelete = (record) => {
        confirm({
            title: 'Confirm role deletion',
            icon: <ExclamationCircleOutlined />,
            content: `Are you sure you want to delete the role "${record.name}"? This action cannot be undone.`,
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk() {
                dispatch(deleteRole(record.id)).then((result) => {
                    if (result.success) {
                        message.success('Role deleted successfully');
                        dispatch(fetchRoles()); // Refresh roles list
                    } else {
                        message.error(result.error || 'Unable to delete role');
                    }
                });
            }
        });
    };

    const getStatusColor = (isActive) => {
        return isActive ? 'green' : 'red';
    };

    const getStatusText = (isActive) => {
        return isActive ? 'Active' : 'Inactive';
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80,
            sorter: (a, b) => a.id - b.id,
        },
        {
            title: 'Role Name',
            dataIndex: 'name',
            key: 'name',
            sorter: (a, b) => a.name.localeCompare(b.name),
            render: (text, record) => (
                <Space>
                    <SafetyOutlined style={{ color: '#1890ff' }} />
                    <strong>{text}</strong>
                </Space>
            ),
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: {
                showTitle: false,
            },
            render: (description) => (
                <Tooltip placement="topLeft" title={description}>
                    <Text type="secondary">{description || 'No description'}</Text>
                </Tooltip>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'isActive',
            key: 'isActive',
            width: 120,
            filters: [
                { text: 'Active', value: true },
                { text: 'Inactive', value: false },
            ],
            onFilter: (value, record) => record.isActive === value,
            render: (isActive) => (
                <Tag color={getStatusColor(isActive)}>
                    {getStatusText(isActive)}
                </Tag>
            ),
        },
        {
            title: 'Created Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 180,
            sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
            render: (date) => date ? new Date(date).toLocaleString('en-US') : '-',
        },
        {
            title: 'Updated Date',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            width: 180,
            sorter: (a, b) => new Date(a.updatedAt) - new Date(b.updatedAt),
            render: (date) => date ? new Date(date).toLocaleString('en-US') : '-',
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 150,
            fixed: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="Edit">
                        <Button
                            type="link"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                            size="small"
                        />
                    </Tooltip>
                    <Tooltip title="Delete">
                        <Button
                            type="link"
                            icon={<DeleteOutlined />}
                            onClick={() => handleDelete(record)}
                            size="small"
                            danger
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <div className="role-management">
            <Row justify='end' style={{ marginBottom: 10, marginTop: 10 }}>
                <Col>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAdd}
                    >
                        Add New Role
                    </Button>
                </Col>
            </Row>
            <Table
                columns={columns}
                dataSource={rolesArray.map(role => ({ ...role, key: role.id }))}
                loading={loading}
                className="role-table"
                pagination={{ pageSize: 10 }}
                scroll={{ x: 1200 }}
            />

            {/* Add/Edit Role Modal */}
            <Modal
                title={editingRole ? 'Edit Role' : 'Add New Role'}
                open={isModalVisible}
                onOk={handleSave}
                onCancel={() => setIsModalVisible(false)}
                okText={editingRole ? 'Update' : 'Create'}
                cancelText="Cancel"
                confirmLoading={loading}
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    name="roleForm"
                >
                    <Form.Item
                        name="name"
                        label="Role Name"
                        rules={[
                            { required: true, message: 'Please enter role name!' },
                            { min: 2, message: 'Role name must be at least 2 characters!' },
                            { max: 50, message: 'Role name cannot exceed 50 characters!' }
                        ]}
                    >
                        <Input placeholder="Enter role name (e.g., Manager, Staff, Viewer...)" />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="Role Description"
                        rules={[
                            { max: 200, message: 'Description cannot exceed 200 characters!' }
                        ]}
                    >
                        <Input.TextArea
                            placeholder="Enter detailed description of this role..."
                            rows={3}
                            showCount
                            maxLength={200}
                        />
                    </Form.Item>

                    <Form.Item
                        name="isActive"
                        label="Status"
                        valuePropName="checked"
                        initialValue={true}
                    >
                        <Switch
                            checkedChildren="Active"
                            unCheckedChildren="Inactive"
                            defaultChecked
                        />
                    </Form.Item>
                </Form>

                <Divider />

                <div style={{ color: '#666', fontSize: '12px' }}>
                    <Text type="secondary">
                        <strong>Note:</strong> After creating a role, you can configure detailed permissions for this role in the "Permission Management" section.
                    </Text>
                </div>
            </Modal>
        </div>
    );
};

export default RoleManagement;
